<script setup>
import { ref, computed, onMounted } from 'vue'
import SettingsLayout from '@/Layouts/SettingsLayout.vue'
import SettingsPricingCard from '@/Components/Forms/SettingsPricingCard.vue'

const props = defineProps({
  user: Object,
  teachingMethods: Array,
  pricingLimits: Object,
})

// Sort teaching methods by ID
const sortedTeachingMethods = computed(() => {
  return [...(props.teachingMethods || [])].sort((a, b) => a.id - b.id)
})

const loading = ref(false)
const successMessage = ref('')
const errors = ref({})

// Teaching methods selection - Initialize with user's methods
const selectedMethods = ref(props.user?.teachingMethods?.map(m => m.id) || [])

// Pricing state - default to average from TutorSettings
const basePlanPrice = ref(props.pricingLimits?.base?.average ?? Math.round(((props.pricingLimits?.base?.min ?? 5) + (props.pricingLimits?.base?.max ?? 25)) / 2))
const silverPlanPrice = ref(props.pricingLimits?.silver?.average ?? Math.round(((props.pricingLimits?.silver?.min ?? 20) + (props.pricingLimits?.silver?.max ?? 80)) / 2))
const goldPlanPrice = ref(props.pricingLimits?.gold?.average ?? Math.round(((props.pricingLimits?.gold?.min ?? 100) + (props.pricingLimits?.gold?.max ?? 200)) / 2))

// Determine which plans to show based on selected teaching methods
const selectedMethodNames = computed(() => {
  return props.teachingMethods
    .filter(m => selectedMethods.value.includes(m.id))
    .map(m => m.name.toLowerCase())
})

const showBasePlan = computed(() => {
  return selectedMethodNames.value.some(name =>
    name.includes('study material') || name.includes('recorded') || name.includes('video')
  )
})

const showSilverPlan = computed(() => {
  return selectedMethodNames.value.some(name => name.includes('group'))
})

const showGoldPlan = computed(() => {
  return selectedMethodNames.value.some(name =>
    name.includes('1:1') || name.includes('one-to-one') || name.includes('1-on-1')
  )
})

// Get teaching methods for each plan type
const basePlanMethods = computed(() => {
  return props.user?.teachingMethods?.filter(m =>
    m.name.toLowerCase().includes('study material') ||
    m.name.toLowerCase().includes('recorded') ||
    m.name.toLowerCase().includes('video')
  ) || []
})

const silverPlanMethods = computed(() => {
  return props.user?.teachingMethods?.filter(m =>
    m.name.toLowerCase().includes('group')
  ) || []
})

const goldPlanMethods = computed(() => {
  return props.user?.teachingMethods?.filter(m =>
    m.name.toLowerCase().includes('1:1') ||
    m.name.toLowerCase().includes('one-to-one') ||
    m.name.toLowerCase().includes('1-on-1')
  ) || []
})

// Load existing data
onMounted(() => {
  // Load existing pricing tiers
  const tiers = props.user?.pricingTiers || props.user?.pricing_tiers
  if (tiers && tiers.length > 0) {
    tiers.forEach(tier => {
      if (tier.tier === 'base' || tier.tier === 'Base') {
        basePlanPrice.value = parseFloat(tier.price)
      } else if (tier.tier === 'silver' || tier.tier === 'Silver') {
        silverPlanPrice.value = parseFloat(tier.price)
      } else if (tier.tier === 'gold' || tier.tier === 'Gold') {
        goldPlanPrice.value = parseFloat(tier.price)
      }
    })
  }
})

// Effective slider limits — expand range if the current price falls outside TutorSettings bounds
const effectiveLimits = computed(() => ({
  base: {
    min: Math.min(props.pricingLimits.base.min, basePlanPrice.value),
    max: Math.max(props.pricingLimits.base.max, basePlanPrice.value),
  },
  silver: {
    min: Math.min(props.pricingLimits.silver.min, silverPlanPrice.value),
    max: Math.max(props.pricingLimits.silver.max, silverPlanPrice.value),
  },
  gold: {
    min: Math.min(props.pricingLimits.gold.min, goldPlanPrice.value),
    max: Math.max(props.pricingLimits.gold.max, goldPlanPrice.value),
  },
}))

const savePricing = async () => {
  loading.value = true
  errors.value = {}
  successMessage.value = ''

  try {
    const response = await fetch('/teacher/settings/pricing', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      },
      body: JSON.stringify({
        teaching_method_ids: selectedMethods.value,
        base_plan_price: basePlanPrice.value,
        silver_plan_price: silverPlanPrice.value,
        gold_plan_price: goldPlanPrice.value,
        offer_base_plan: showBasePlan.value,
        offer_silver_plan: showSilverPlan.value,
        offer_gold_plan: showGoldPlan.value,
      })
    })

    const data = await response.json()

    if (!response.ok) {
      if (data.errors) {
        errors.value = Object.keys(data.errors).reduce((acc, key) => {
          acc[key] = Array.isArray(data.errors[key]) ? data.errors[key][0] : data.errors[key]
          return acc
        }, {})
      } else {
        errors.value = { general: data.message || 'Failed to save. Please try again.' }
      }
      loading.value = false
      return
    }

    successMessage.value = 'Pricing updated successfully!'
    loading.value = false

    setTimeout(() => {
      successMessage.value = ''
    }, 3000)

  } catch (err) {
    errors.value = { general: 'An error occurred. Please try again.' }
    loading.value = false
  }
}
</script>

<template>
  <SettingsLayout>
    <div class="space-y-6">
      <!-- Success Message -->
      <div v-if="successMessage" class="bg-green-50 border-2 border-green-200 rounded-2xl p-5 flex items-center">
        <div class="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mr-4">
          <span class="material-symbols-outlined text-white text-xl">check_circle</span>
        </div>
        <div>
          <p class="text-green-900 font-semibold">Success</p>
          <p class="text-green-700 text-sm">{{ successMessage }}</p>
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="errors.general" class="bg-red-50 border-2 border-red-200 rounded-2xl p-5 flex items-center">
        <div class="flex-shrink-0 w-10 h-10 bg-red-500 rounded-full flex items-center justify-center mr-4">
          <span class="material-symbols-outlined text-white text-xl">error</span>
        </div>
        <div>
          <p class="text-red-900 font-semibold">Error</p>
          <p class="text-red-700 text-sm">{{ errors.general }}</p>
        </div>
      </div>

      <!-- Teaching Methods Selection - Simplified -->
      <div class="bg-white rounded-2xl p-8 border-2 border-gray-200">
        <div class="mb-6">
          <h2 class="text-2xl font-bold text-gray-900 mb-2 flex items-center">
            <span class="material-symbols-outlined text-brand-blue mr-3">school</span>
            Your Teaching Methods
          </h2>
          <p class="text-gray-600">These are the teaching methods you offer. Selected methods are highlighted.</p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div
            v-for="method in sortedTeachingMethods"
            :key="method.id"
            :class="[
              'relative inline-flex items-center gap-3 px-4 py-4 border-2 rounded-xl transition-all overflow-hidden',
              selectedMethods.includes(method.id)
                ? 'bg-white border-gray-300 shadow-sm'
                : 'bg-gray-50 border-gray-200 opacity-60'
            ]"
          >
            <!-- Top-right corner indicator for selected methods -->
            <div
              v-if="selectedMethods.includes(method.id)"
              class="absolute top-0 right-0 w-0 h-0"
              :style="{
                borderStyle: 'solid',
                borderWidth: '0 40px 40px 0',
                borderColor: `transparent ${method.color} transparent transparent`
              }"
            ></div>

            <!-- Checkmark icon in the corner -->
            <span
              v-if="selectedMethods.includes(method.id)"
              class="material-symbols-outlined absolute top-1 right-1 text-white text-sm z-10"
              style="font-size: 14px;"
            >
              check
            </span>

            <div
              class="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
              :style="{ backgroundColor: `${method.color}20` }"
            >
              <span
                class="material-symbols-outlined text-2xl"
                :style="{ color: method.color }"
              >
                {{ method.icon_name }}
              </span>
            </div>
            <div class="flex-1">
              <span class="text-base font-semibold text-gray-900 block">{{ method.name }}</span>
              <span class="text-xs text-gray-500">{{ method.description }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Pricing Cards - Stacked Full Width -->
      <div class="space-y-6">
        <!-- Base Plan -->
        <SettingsPricingCard
          v-if="showBasePlan"
          v-model:price="basePlanPrice"
          title="Base Plan"
          subtitle="Video content & study materials"
          icon="play_circle"
          :features="[
            { text: 'Recorded video lessons', bold: false },
            { text: 'Downloadable study materials', bold: false },
            { text: 'Practice worksheets', bold: false },
            { text: 'Access to resource library', bold: false },
          ]"
          :min-price="effectiveLimits.base.min"
          :max-price="effectiveLimits.base.max"
          :recommended-min="Math.round(pricingLimits.base.average * 0.8)"
          :recommended-max="Math.round(pricingLimits.base.average * 1.2)"
          border-color="border-blue-200"
          icon-bg-color="bg-blue-100"
          icon-text-color="text-blue-600"
          slider-accent-color="accent-blue-600"
          price-bg-color="bg-gray-50"
          :teaching-methods="basePlanMethods"
          :show-teaching-methods="true"
        />

        <!-- Silver Plan -->
        <SettingsPricingCard
          v-if="showSilverPlan"
          v-model:price="silverPlanPrice"
          title="Silver Plan"
          subtitle="Base + Weekly group sessions"
          icon="groups"
          :features="[
            { text: 'Everything in Base plan', bold: false },
            { text: 'Weekly group tutoring sessions', bold: true },
            { text: 'Live Q&A sessions', bold: false },
            { text: 'Community forum access', bold: false },
          ]"
          :min-price="effectiveLimits.silver.min"
          :max-price="effectiveLimits.silver.max"
          :recommended-min="Math.round(pricingLimits.silver.average * 0.8)"
          :recommended-max="Math.round(pricingLimits.silver.average * 1.2)"
          border-color="border-purple-200"
          icon-bg-color="bg-purple-100"
          icon-text-color="text-purple-600"
          slider-accent-color="accent-purple-600"
          price-bg-color="bg-purple-50"
          :teaching-methods="silverPlanMethods"
          :show-teaching-methods="true"
        />

        <!-- Gold Plan -->
        <SettingsPricingCard
          v-if="showGoldPlan"
          v-model:price="goldPlanPrice"
          title="Gold Plan"
          subtitle="Base + Weekly 1:1 sessions"
          icon="workspace_premium"
          :features="[
            { text: 'Everything in Base plan', bold: false },
            { text: 'Weekly 1:1 tutoring sessions', bold: true },
            { text: 'Personalized lesson plans', bold: false },
            { text: 'Priority email support', bold: false },
          ]"
          :min-price="effectiveLimits.gold.min"
          :max-price="effectiveLimits.gold.max"
          :recommended-min="Math.round(pricingLimits.gold.average * 0.8)"
          :recommended-max="Math.round(pricingLimits.gold.average * 1.2)"
          border-color="border-amber-200"
          icon-bg-color="bg-gradient-to-br from-amber-100 to-yellow-100"
          icon-text-color="text-amber-600"
          slider-accent-color="accent-amber-600"
          price-bg-color="bg-gradient-to-br from-amber-50 to-yellow-50"
          :teaching-methods="goldPlanMethods"
          :show-teaching-methods="true"
        />
      </div>

    </div>
  </SettingsLayout>
</template>
